import {
  forwardRef,
  useState,
  type InputHTMLAttributes,
  useId,
} from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
  baseInputStyles,
  fieldHeightStyles,
  inputSizeStyles,
  inputStateStyles,
  type BaseInputProps,
} from './fieldStyles';
import { FieldLabel } from './FieldLabel';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    BaseInputProps {}

// ============================================================================
// Input Component
// ============================================================================

/**
 * Text input component
 *
 * @example
 * ```tsx
 * // Basic input
 * <Input label="Email" type="email" placeholder="Enter email" />
 *
 * // With error
 * <Input label="Password" type="password" error="Password is required" />
 *
 * // With icons
 * <Input
 *   label="Search"
 *   leftIcon={<SearchIcon />}
 *   placeholder="Search..."
 * />
 *
 * // With react-hook-form
 * <Input
 *   label="Name"
 *   {...register('name')}
 *   error={errors.name?.message}
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      leftIcon,
      icon,
      rightIcon,
      inputSize = 'md',
      fullWidth = true,
      wrapperClassName = '',
      className = '',
      id: providedId,
      type = 'text',
      grayBackground = false,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = providedId ?? generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    const hasError = !!error;
    const isPassword = type === 'password';
    const [showPassword, setShowPassword] = useState(false);

    // Support both leftIcon and icon (alias)
    const inputLeftIcon = leftIcon ?? icon;

    // For password fields, the toggle button occupies the right icon slot
    const hasRightElement = rightIcon || isPassword;

    const grayBgStyles = grayBackground
      ? 'bg-[var(--color-bg-secondary)] border-transparent focus:bg-[var(--color-bg-primary)] focus:border-accent-500'
      : '';

    const inputClasses = [
      baseInputStyles,
      inputSizeStyles[inputSize],
      // Высота — общая с кнопкой; отступы и кегль остаются от размера поля.
      fieldHeightStyles[inputSize],
      grayBackground ? grayBgStyles : (hasError ? inputStateStyles.error : inputStateStyles.default),
      hasError && grayBackground ? 'border-[var(--color-error)]' : '',
      inputLeftIcon ? 'pl-10' : '',
      hasRightElement ? 'pr-10' : '',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    const wrapperClasses = [
      fullWidth ? 'w-full' : '',
      wrapperClassName,
    ].join(' ');

    // Determine the actual input type
    const resolvedType = isPassword && showPassword ? 'text' : type;

    return (
      <div className={wrapperClasses}>
        <FieldLabel htmlFor={inputId} label={label} required={required} />

        <div className="relative">
          {inputLeftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-[var(--color-text-disabled)]">
              {inputLeftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            className={inputClasses}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? errorId : helperText ? helperId : undefined
            }
            onWheel={type === 'number' ? (e) => (e.target as HTMLInputElement).blur() : undefined}
            {...props}
          />

          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--color-text-disabled)] hover:text-[var(--color-text-secondary)] transition-colors duration-150"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
            >
              {showPassword ? (
                <Eye className="h-5 w-5" />
              ) : (
                <EyeOff className="h-5 w-5" />
              )}
            </button>
          )}

          {rightIcon && !isPassword && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-[var(--color-text-disabled)]">
              {rightIcon}
            </div>
          )}
        </div>

        {hasError && typeof error === 'string' && (
          <p id={errorId} className="mt-1 text-sm text-[var(--color-error)]">
            {error}
          </p>
        )}

        {helperText && !hasError && (
          <p id={helperId} className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
